"use client";

import { useEffect, useRef } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { useGetWinnersListQuery } from "@/app/redux/features/api/winners";
import { hasActiveSubscription } from "../../../helpers/hasActiveSubscription";
import CategorySection from "./components/CategorySection";
import CategoryTabs from "./components/CategoryTabs";
import Specs from "./components/Specs";
import Loader from "./loader";
import NoWinners from "./noWinners";
import { filterWinnersByCategory, SUBSCRIBER_COMPETITION_KEY } from "./category-utils";
import useDeferredReviveAds from "@/app/hooks/useDeferredReviveAds";

const Winners = () => {
  const { data, isLoading } = useGetWinnersListQuery();
  const reviveAdBanner = useRef<HTMLDivElement>(null);

  useDeferredReviveAds();

  useEffect(() => {
    const handleAdsLoaded = (e: any) => {
      const detail = e?.detail;
      if (!detail) return;
      const hasAdsToLoad = Object.values(detail).some((ad: any) => Number(ad.width) > 0 || Number(ad.height) > 0);

      if (hasAdsToLoad) {
        const adHeight = document.querySelector(".winner-mobile-ad")?.clientHeight || 100;
        const target = document.querySelector("#winners-container") as HTMLElement | null;
        if (target) {
          target.style.paddingBottom = `${adHeight - 20}px`;
        }

        const footer = document.querySelector(".footer-v2") as HTMLElement | null;
        if (footer) {
          if (window.innerWidth < 1024) {
            footer.style.paddingBottom = `${adHeight}px`;
          } else {
            footer.style.paddingBottom = "";
          }
        }
      }
    };

    const handleResize = () => {
      const footer = document.querySelector(".footer-v2") as HTMLElement | null;
      if (!footer) return;

      if (window.innerWidth < 1024) {
        const adHeight = document.querySelector(".winner-mobile-ad")?.clientHeight || 100;
        footer.style.paddingBottom = `${adHeight}px`;
      } else {
        footer.style.paddingBottom = "";
      }
    };
    document.addEventListener("revive-6ee315d200a003a5ac39b07d0dd34156-completed", handleAdsLoaded);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      document.removeEventListener("revive-6ee315d200a003a5ac39b07d0dd34156-completed", handleAdsLoaded);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const hasWinners = !isLoading && data?.winnerList && Array.isArray(data.winnerList) && data.winnerList.length > 0;

  return (
    <>
      <div id={"script_ad_server"} className={"hidden"} />
      <div ref={reviveAdBanner} className={"hidden"} />

      {isLoading ? (
        <Loader />
      ) : !hasWinners ? (
        <NoWinners />
      ) : (
        <>
          <section className="mx-auto mt-5 pt-12 lg:pt-0 mainSection">
            <div className="flex flex-row lg:flex-col items-center justify-between lg:justify-center gap-3 lg:gap-4 px-3">
              <h1
                data-testid="winners-title"
                className="!mb-0 min-w-0 normal-case text-left lg:text-center text-dark-1 font-jost text-2xl lg:text-[32px] font-semibold leading-[30px] lg:leading-10">
                {data.title}
              </h1>

              <Specs />
            </div>
          </section>

          {/* Category menu: mobile fixed below header, desktop sticky below header on scroll */}
          <div
            className="bg-white drop-shadow-[0px_1.5px_2px_rgba(0,0,0,0.1)] max-lg:fixed max-lg:left-0 max-lg:right-0 max-lg:z-40 lg:sticky lg:z-40 lg:mt-7"
            style={{ top: "var(--header-height)" }}>
            <div className="relative lg:px-8">
              <div className="w-full max-w-[1360px] mx-auto">
                <CategoryTabs categories={data.winnerCategories ?? []} />
              </div>
            </div>
          </div>

          <div className="lg:px-8 mt-6 lg:mt-0 py-6 lg:py-10 bg-gray-4">
            <section className="mx-auto mainSection">
              <div className="flex flex-col gap-8">
                {/* mobile wide */}
                <ins
                  data-revive-usersubscriber={hasActiveSubscription()}
                  data-revive-zoneid="8"
                  data-revive-source="WL_wide01_mob"
                  data-revive-id="6ee315d200a003a5ac39b07d0dd34156"
                  className={"lg:hidden [&:empty]:hidden fixed-theme z-[20] bottom-0 inset-x-0 winner-mobile-ad"}></ins>

                {/* desktop wide */}
                <ins
                  data-revive-usersubscriber={hasActiveSubscription()}
                  data-revive-zoneid="7"
                  data-revive-source="WL_wide01_desk"
                  data-revive-id="6ee315d200a003a5ac39b07d0dd34156"
                  className={"max-lg:hidden [&:empty]:hidden block winner-desktop-ad"}></ins>

                {(data.winnerCategories ?? []).map((cat, categoryIndex) => (
                  <CategorySection
                    key={cat.Key}
                    categoryIndex={categoryIndex}
                    category={cat}
                    winners={(() => {
                      const subscriberCompetition = data.winnerCategories.find(
                        c => c.Key === SUBSCRIBER_COMPETITION_KEY
                      );
                      return filterWinnersByCategory(data.winnerList, cat.Key).map(item => ({
                        ...item,
                        isSubscriberCompetition: item.WinnerCategories?.includes(subscriberCompetition?.Key ?? ""),
                      }));
                    })()}
                  />
                ))}
              </div>
            </section>
          </div>
        </>
      )}
    </>
  );
};

export default Winners;
